import React, { useMemo } from 'react';
import { parseISO, differenceInDays } from 'date-fns';
import { Card } from '../ui/card';
import { Badge } from '../ui/badge';
import {
  Plane, ChevronDown, ChevronUp, Link, TrendingUp,
  AlertTriangle, Calendar, Clock, Anchor,
} from 'lucide-react';
import { analyzeTripDates } from './tripDateAnalyzer';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../ui/tooltip';
import {
  formatDate, TRIP_TYPE_COLORS, getTripFlags, getTripTitle,
  getBadgeStyle, getBadgeLabel, getIconBg, getTripIcon, getTripLocation,
} from './tripCardHelpers';
import { TripCardExpanded } from './TripCardExpanded';

const TripCard = ({ trip, expanded, onToggle, onEdit, onDelete, onMarkReturned, allTrips = [], linkedDocs = [], onAttachDoc, onPreviewDoc, onUnlinkDoc }) => {
  const f = getTripFlags(trip);
  const preUkStops = trip.pre_uk_stops || [];
  const preAbroadStops = trip.pre_abroad_stops || [];
  const postAbroadStops = trip.post_abroad_stops || [];
  const postUkStops = trip.post_uk_stops || [];
  const isPartialTaxYear = trip.partial_tax_year?.is_partial;
  const borderColor = TRIP_TYPE_COLORS[trip.trip_type] || 'border-l-slate-500';

  // Check if this is a future trip
  const isFutureTrip = useMemo(() => {
    const startDate = trip.date_left_uk || trip.date_joined_vessel;
    if (!startDate) return false;
    try {
      return parseISO(startDate) > new Date();
    } catch (err) {
      return false;
    }
  }, [trip.date_left_uk, trip.date_joined_vessel]);

  // Ongoing: has a departure in the past but no return logged yet
  const isOngoing = useMemo(() => {
    const hasReturn = (trip.date_returned_uk && trip.date_returned_uk.trim())
      || (trip.date_left_vessel && trip.date_left_vessel.trim());
    const startDate = trip.date_left_uk || trip.date_joined_vessel;
    if (hasReturn || !startDate || isFutureTrip) return false;
    const excluded = ['wage_adjustment', 'paid_leave', 'qualifying_holiday', 'visa_run'];
    if (excluded.includes(trip.trip_type)) return false;
    try {
      return parseISO(startDate) <= new Date();
    } catch (err) {
      return false;
    }
  }, [trip.date_returned_uk, trip.date_left_vessel, trip.date_left_uk, trip.date_joined_vessel, trip.trip_type, isFutureTrip]);

  const dateWarnings = useMemo(() => analyzeTripDates(trip), [trip]);

  // Check if there's an adjoining trip (another trip starts on this trip's end date)
  const hasAdjoiningTrip = () => {
    const tripEnd = trip.date_returned_uk || trip.date_left_vessel;
    const tripStart = trip.date_left_uk || trip.date_joined_vessel;
    if (!tripEnd || !tripStart) return false;

    return allTrips.some(otherTrip => {
      if (otherTrip.id === trip.id) return false;
      const otherStart = otherTrip.date_left_uk || otherTrip.date_joined_vessel;
      return otherStart === tripEnd;
    });
  };

  const getTotalDays = () => {
    // For multi-leg trips, use vessel dates for the full trip duration
    const start = f.isMultiLeg
      ? (trip.date_joined_vessel || trip.date_left_uk)
      : (trip.date_left_uk || trip.date_joined_vessel);
    const end = f.isMultiLeg
      ? (trip.date_left_vessel || trip.date_returned_uk)
      : (trip.date_returned_uk || trip.date_left_vessel);
    if (!start || !end) return 0;
    try {
      let days = differenceInDays(parseISO(end), parseISO(start)) + 1;
      // For UK hotel stays with an adjoining trip, subtract 1 day
      // (the check-out day is counted by the adjoining trip instead)
      if (f.isUkHotelStay && hasAdjoiningTrip()) {
        days = days - 1;
      }
      return days > 0 ? days : 0;
    } catch (err) {
      return 0;
    }
  };

  const totalDays = getTotalDays();

  const getDateRange = () => {
    if (f.isMultiLeg && trip.date_joined_vessel && trip.date_left_vessel) {
      return `${formatDate(trip.date_joined_vessel)} to ${formatDate(trip.date_left_vessel)} (vessel dates)`;
    }

    let leftLabel = trip.date_left_uk ? formatDate(trip.date_left_uk) : '';
    let returnLabel = trip.date_returned_uk ? formatDate(trip.date_returned_uk) : '';

    if (trip.no_uk_departure && !trip.date_left_uk) {
      leftLabel = '(Already abroad)';
    } else if (trip.no_uk_departure && trip.date_left_uk) {
      leftLabel = `${formatDate(trip.date_left_uk)} (Already abroad)`;
    }

    if (trip.no_uk_return && !trip.date_returned_uk) {
      returnLabel = '(Stayed abroad)';
    } else if (trip.no_uk_return && trip.date_returned_uk) {
      returnLabel = `${formatDate(trip.date_returned_uk)} (Stayed abroad)`;
    }

    return `${leftLabel} to ${returnLabel}`;
  };

  const location = getTripLocation(trip, f);

  return (
    <Card className={`${isFutureTrip ? 'bg-red-950/20 border-red-500/20' : 'bg-slate-800 border-slate-700'} overflow-hidden border-l-4 ${borderColor}`} data-testid={`trip-card-${trip.id}`}>
      <div
        className="cursor-pointer hover:bg-slate-700/30 transition-colors"
        onClick={onToggle}
      >
        {/* Main Card Content */}
        <div className="p-4 sm:p-5">
          {/* Top Row: Title and Key Stats */}
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4">
            <div className="flex items-center gap-3">
              <div className={`p-2 sm:p-2.5 rounded-xl ${getIconBg(f)} flex-shrink-0`}>
                {getTripIcon(f)}
              </div>
              <div className="min-w-0">
                <div className="flex flex-wrap items-center gap-2">
                  <h3 className="text-base sm:text-lg font-semibold text-white truncate">{getTripTitle(trip, f)}</h3>
                  <Badge variant="outline" className={`${getBadgeStyle(f)} text-xs flex-shrink-0`}>
                    {getBadgeLabel(f)}
                  </Badge>
                  {dateWarnings.hasWarnings && (
                    <Badge variant="outline" className="border-amber-500/50 text-amber-400 text-[10px] flex-shrink-0 gap-1" data-testid="date-warning-badge">
                      <AlertTriangle className="h-3 w-3" />
                      {dateWarnings.gaps.length > 0 ? `${dateWarnings.gaps.length} gap${dateWarnings.gaps.length !== 1 ? 's' : ''}` : 'Date issue'}
                    </Badge>
                  )}
                  {isFutureTrip && (
                    <Badge variant="outline" className="border-red-400/50 bg-red-500/10 text-red-400 text-[10px] flex-shrink-0 gap-1" data-testid="future-trip-badge">
                      <Clock className="h-3 w-3" />
                      Future
                    </Badge>
                  )}
                  {isOngoing && (
                    <Badge variant="outline" className="border-sky-400/50 bg-sky-500/10 text-sky-400 text-[10px] flex-shrink-0 gap-1" data-testid="ongoing-trip-badge">
                      <Anchor className="h-3 w-3" />
                      {f.isWorkTrip ? 'Currently onboard' : (f.isHotelStay || f.isCourseForeign || trip.trip_type === 'base_work_foreign') ? 'Currently abroad' : 'In progress'}
                    </Badge>
                  )}
                </div>
                {/* Show contractor for work trips, location for hotels/holidays */}
                {trip.contractor && f.isWorkTrip && (
                  <p className="text-xs sm:text-sm text-slate-500 mt-0.5 truncate">{trip.contractor}</p>
                )}
                {location && (
                  <p className="text-xs sm:text-sm text-amber-400 mt-0.5 truncate">{location}</p>
                )}
              </div>
            </div>

            {/* Right: Expand/Collapse */}
            <div className="flex items-center">
              <div className="text-slate-500">
                {expanded ? <ChevronUp className="h-5 w-5" /> : <ChevronDown className="h-5 w-5" />}
              </div>
            </div>
          </div>

          {/* Bottom Row: Date Range and Days Stats */}
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 pt-3 border-t border-slate-700/50">
            <div className="flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-6">
              <div>
                <p className="text-xs text-slate-500 uppercase tracking-wide mb-1">Period</p>
                <p className="text-xs sm:text-sm text-slate-300 font-medium">{getDateRange()}</p>
              </div>

              <div className="flex flex-wrap items-center gap-2">
                {f.isContinuous && !f.isUkBased && (
                  <Badge className="bg-cyan-500/10 text-cyan-400 border-cyan-500/30 text-xs">
                    <Link className="h-3 w-3 mr-1" />
                    Continuous
                  </Badge>
                )}
                {f.isMultiLeg && (
                  <Badge className="bg-amber-500/10 text-amber-400 border-amber-500/30 text-xs">
                    {trip.uk_water_crossings.length + 1} Legs
                  </Badge>
                )}
                {trip.uk_waters_status === 'uk_only' && (
                  <Badge className="bg-blue-500/10 text-blue-400 border-blue-500/30 text-xs" data-testid="uk-waters-badge">
                    UK Waters Only
                  </Badge>
                )}
                {f.hasPreTravel && (
                  <Badge className="bg-purple-500/10 text-purple-400 border-purple-500/30 text-xs">
                    <Plane className="h-3 w-3 mr-1" />
                    Pre-trip {preUkStops.length > 0 ? 'UK ' : ''}{preAbroadStops.length > 0 ? '+ Abroad' : ''}
                  </Badge>
                )}
                {f.hasPostTravel && (
                  <Badge className="bg-purple-500/10 text-purple-400 border-purple-500/30 text-xs">
                    Post-trip {postAbroadStops.length > 0 ? 'Abroad ' : ''}{postUkStops.length > 0 ? '+ UK' : ''}
                    <Plane className="h-3 w-3 ml-1" />
                  </Badge>
                )}
                {trip.overlap_info?.has_overlap && (
                  <Badge className="bg-amber-500/10 text-amber-400 border-amber-500/30 text-xs">
                    Shared Day
                  </Badge>
                )}
                {isPartialTaxYear && (
                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Badge
                          className="bg-purple-500/10 text-purple-400 border-purple-500/30 text-xs cursor-help"
                          data-testid="partial-tax-year-badge"
                        >
                          <Calendar className="h-3 w-3 mr-1" />
                          Spans Tax Year
                        </Badge>
                      </TooltipTrigger>
                      <TooltipContent className="bg-slate-800 border-slate-700 text-slate-200 max-w-xs">
                        <div className="space-y-2">
                          <p className="font-medium text-purple-400">Tax Year Boundary Trip</p>
                          <p className="text-xs text-slate-400">
                            This trip crosses the April 6th tax year boundary.
                          </p>
                          <div className="text-xs space-y-1">
                            <p><span className="text-slate-500">Full trip:</span> {trip.partial_tax_year.full_trip_qualifying_days} qualifying days</p>
                            <p><span className="text-slate-500">This tax year:</span> {trip.partial_tax_year.tax_year_qualifying_days} qualifying days</p>
                            <p className="text-slate-500 pt-1 border-t border-slate-700">
                              Dates in this tax year: {formatDate(trip.partial_tax_year.effective_start)} - {formatDate(trip.partial_tax_year.effective_end)}
                            </p>
                          </div>
                        </div>
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                )}
                {trip.uplifts && trip.uplifts.length > 0 && (
                  <Badge className="bg-amber-500/10 text-amber-400 border-amber-500/30 text-xs" data-testid="uplifts-badge">
                    <TrendingUp className="h-3 w-3 mr-1" />
                    {trip.uplifts.length} {trip.uplifts.length === 1 ? 'Uplift' : 'Uplifts'}
                  </Badge>
                )}
              </div>
            </div>

            {/* Right: Days Stats */}
            <div className="flex items-center gap-2 sm:gap-4">
              {totalDays && (
                <div className="bg-slate-700/50 rounded-lg px-3 sm:px-4 py-1.5 sm:py-2 text-center flex-1 sm:flex-none sm:min-w-[80px]">
                  <p className="text-[10px] sm:text-xs text-slate-500 uppercase tracking-wide">Duration</p>
                  <p className="text-base sm:text-lg font-bold text-blue-400">{totalDays} <span className="text-[10px] sm:text-xs font-normal text-slate-500">days</span></p>
                </div>
              )}
              <div className={`rounded-lg px-3 sm:px-4 py-1.5 sm:py-2 text-center flex-1 sm:flex-none sm:min-w-[80px] ${f.isUkBased ? 'bg-slate-700/30' : 'bg-amber-500/10'}`}>
                <p className="text-[10px] sm:text-xs text-slate-500 uppercase tracking-wide">Qualifying</p>
                <p className={`text-base sm:text-lg font-bold ${f.isUkBased ? 'text-slate-500' : 'text-amber-400'}`}>
                  {trip.qualifying_days} <span className="text-[10px] sm:text-xs font-normal text-slate-500">days</span>
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Expanded Details */}
      {expanded && (
        <TripCardExpanded
          trip={trip} flags={f} dateWarnings={dateWarnings} isOngoing={isOngoing}
          linkedDocs={linkedDocs}
          onEdit={onEdit} onDelete={onDelete} onMarkReturned={onMarkReturned}
          onAttachDoc={onAttachDoc} onPreviewDoc={onPreviewDoc} onUnlinkDoc={onUnlinkDoc}
        />
      )}
    </Card>
  );
};

export default TripCard;
